import { type HTMLAttributes } from 'react';
import { formatMoney } from '@bravemove/i18n/format';
import { cn } from '../../utils/cn';

export interface CurrencyDisplayProps extends HTMLAttributes<HTMLSpanElement> {
  /** Amount in MINOR units (USD cents · LBP whole pounds). Same convention as `@bravemove/types`. */
  readonly amount: number;
  readonly currency: string;
  /** Optional secondary display (e.g. LBP equivalent for a USD primary). */
  readonly secondary?:
    | {
        readonly amount: number;
        readonly currency: string;
      }
    | undefined;
  readonly locale?: string;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE: Record<
  NonNullable<CurrencyDisplayProps['size']>,
  { primary: string; secondary: string }
> = {
  sm: { primary: 'text-[12.5px]', secondary: 'text-[10.5px]' },
  md: { primary: 'text-[14px]', secondary: 'text-[11px]' },
  lg: { primary: 'text-[18px]', secondary: 'text-[12px]' },
  xl: { primary: 'text-[24px]', secondary: 'text-[13px]' },
};

/**
 * BraveMove money renderer. USD primary, LBP optional secondary (or any pair).
 * Amounts are always passed in MINOR units (USD cents, LBP whole pounds) —
 * matches `packages/types` Money convention. Per-currency fraction digits are
 * resolved by `formatMoney` from `@bravemove/i18n/format`.
 */
export function CurrencyDisplay({
  amount,
  currency,
  secondary,
  locale = 'en-US',
  size = 'md',
  className,
  ...rest
}: CurrencyDisplayProps) {
  const sz = SIZE[size];
  return (
    <span
      className={cn('inline-flex items-baseline gap-1.5 text-ink tabular-nums', className)}
      {...rest}
    >
      <span className={cn('font-bold', sz.primary)}>
        {formatMoney({ amount, currency }, locale)}
      </span>
      {secondary ? (
        <span className={cn('text-ink-3 font-medium font-mono', sz.secondary)}>
          ≈ {formatMoney(secondary, locale)}
        </span>
      ) : null}
    </span>
  );
}
